import { Landmark, Scale, LineChart, Compass, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const TEAM_IMG =
  "https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwbW9kZXJufGVufDB8fHx8MTc5MTAzNTA0OHww&ixlib=rb-4.1.0&q=85";

const CARDS = [
  {
    icon: Landmark,
    title: "Finance Operations",
    desc: "Day-to-day ledgers, reconciliations and month-end close, kept current in your systems and on your reporting calendar — not ours.",
    fit: "growing companies that need clean, audit-ready books without hiring an in-house team.",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    icon: Scale,
    title: "Governance and Control (SOX Engagements)",
    desc: "Controls documentation, walkthroughs and testing support that stand up to your external auditors, on a schedule that respects deadlines well ahead.",
    fit: "pre-IPO and newly public companies building out or maturing their SOX program.",
    span: "",
  },
  {
    icon: LineChart,
    title: "Virtual CFO Services",
    desc: "Forecasting, board-ready reporting and cash discipline from someone who treats your numbers as decisions, not just entries.",
    fit: "founders and leadership teams who need CFO-level judgment without a full-time hire.",
    span: "",
  },
  {
    icon: Compass,
    title: "GCC Strategy Advisory & Execution",
    desc: "Planning and standing up a Global Capability Centre in India — structure, hiring sequence, compliance and governance — end to end.",
    fit: "companies evaluating or committed to building a standing offshore capability center.",
    span: "md:col-span-3",
    image: true,
  },
];

export default function Services() {
  return (
    <section id="services" className="border-b border-line">
      <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ink/60">
            01 — Services
          </p>
          <h2 className="mt-4 max-w-2xl font-serif text-4xl tracking-tight md:text-5xl">
            What we run for you.
          </h2>
          <h3 className="mt-6 font-serif text-xl font-bold tracking-tight md:text-2xl" data-testid="services-subhead">
            Four engagements, one accountable team
          </h3>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-ink/60">
            Each service can run independently or as one coordinated finance
            function — you choose the depth, we hold the continuity.
          </p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-px border border-line bg-line md:grid-cols-3">
          {CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08} className={`bg-paper ${c.span}`}>
              <div
                className="group flex h-full flex-col justify-between p-8 transition-colors duration-300 hover:bg-white md:p-10"
                data-testid={`service-card-${i + 1}`}
              >
                <div>
                  <div className="flex items-center justify-between">
                    <c.icon className="h-6 w-6 text-ink" strokeWidth={1.5} />
                    <span className="font-mono text-xs tracking-[0.2em] text-ink/40">
                      S.0{i + 1}
                    </span>
                  </div>
                  <h3 className="mt-8 font-serif text-2xl tracking-tight md:text-3xl">
                    {c.title}
                  </h3>
                  <p className="mt-4 max-w-lg text-sm leading-relaxed text-ink/60">
                    {c.desc}
                  </p>
                  <p className="mt-5 max-w-lg text-xs leading-relaxed text-ink/70">
                    <span className="font-medium text-gold">Best fit: </span>
                    {c.fit}
                  </p>
                </div>
                {c.image ? (
                  <div className="mt-8 overflow-hidden border border-ink/15">
                    <img
                      src={TEAM_IMG}
                      alt="Execution team in session"
                      className="h-48 w-full object-cover saturate-110 transition-transform duration-700 group-hover:scale-105 md:h-56"
                      data-testid="services-image"
                    />
                  </div>
                ) : (
                  <ArrowUpRight className="mt-8 h-5 w-5 text-ink/30 transition-colors duration-300 group-hover:text-gold" />
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
